import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Plus, Globe, Phone, Mail, MoreHorizontal, Pencil, Trash2,
  ArrowUp, ArrowDown, ArrowUpDown, Copy, Download, Settings2, RefreshCw, FilterX,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useDebounce } from '@/hooks/useDebounce';
import { useCompanies, useCreateCompany, useUpdateCompany, useDeleteCompany } from '../hooks/useCrmCompanies';
import { getCompanies } from '../api/crmApi';
import { exportToCSV, copyToClipboard } from '../utils/csvExport';
import { FieldDocsTable, COMPANY_FIELDS } from '../components/FieldDocs';
import type { Company, CompanyFilters, CompanyType, CompanyStatus, CreateCompanyInput } from '../types';

// =============================================================================
// Column Definition
// =============================================================================

interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

// =============================================================================
// Constants
// =============================================================================

const TYPE_COLORS: Record<CompanyType, string> = {
  customer: 'bg-green-100 text-green-800',
  prospect: 'bg-blue-100 text-blue-800',
  supplier: 'bg-amber-100 text-amber-800',
  partner: 'bg-purple-100 text-purple-800',
  competitor: 'bg-red-100 text-red-800',
  irrelevant: 'bg-slate-100 text-slate-500',
  other: 'bg-gray-100 text-gray-800',
};

const STATUS_COLORS: Record<CompanyStatus, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-600',
  archived: 'bg-red-100 text-red-800',
};

// =============================================================================
// Column Definitions (built inside component to support t())
// =============================================================================

function buildColumns(t: (key: string) => string): ColumnDef<Company>[] {
  const TYPE_OPTIONS = [
    { label: t('companies.type.customer'), value: 'customer' },
    { label: t('companies.type.prospect'), value: 'prospect' },
    { label: t('companies.type.supplier'), value: 'supplier' },
    { label: t('companies.type.partner'), value: 'partner' },
    { label: t('companies.type.competitor'), value: 'competitor' },
    { label: t('companies.type.irrelevant'), value: 'irrelevant' },
    { label: t('companies.type.other'), value: 'other' },
  ];

  const STATUS_OPTIONS = [
    { label: t('companies.status.active'), value: 'active' },
    { label: t('companies.status.inactive'), value: 'inactive' },
    { label: t('companies.status.archived'), value: 'archived' },
  ];

  return [
  {
    key: 'name',
    label: t('companies.columns.company'),
    defaultVisible: true,
    sortKey: 'name',
    filterType: 'text',
    getValue: (c) => c.name,
    render: (c) => (
      <div>
        <div className="font-medium">{c.name}</div>
        {c.website && (
          <a
            href={c.website.startsWith('http') ? c.website : `https://${c.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <Globe className="h-3 w-3" />
            {c.website}
          </a>
        )}
      </div>
    ),
  },
  {
    key: 'contactCount',
    label: t('companies.columns.contacts'),
    defaultVisible: true,
    sortKey: 'contactCount',
    filterType: 'number-range',
    getValue: (c) => c.contact_count ?? 0,
    render: (c) => (
      <span className={cn('text-sm tabular-nums', (c.contact_count ?? 0) > 0 ? 'font-medium' : 'text-muted-foreground')}>
        {c.contact_count ?? 0}
      </span>
    ),
    align: 'right',
  },
  {
    key: 'type',
    label: t('companies.columns.type'),
    defaultVisible: true,
    filterType: 'select',
    filterOptions: TYPE_OPTIONS,
    getValue: (c) => c.type,
    render: (c) => (
      <Badge variant="outline" className={TYPE_COLORS[c.type]}>{t(`companies.type.${c.type}`)}</Badge>
    ),
  },
  {
    key: 'status',
    label: t('companies.columns.status'),
    defaultVisible: true,
    filterType: 'select',
    filterOptions: STATUS_OPTIONS,
    getValue: (c) => c.status,
    render: (c) => (
      <Badge variant="outline" className={STATUS_COLORS[c.status]}>{t(`companies.status.${c.status}`)}</Badge>
    ),
  },
  {
    key: 'industry',
    label: t('companies.columns.industry'),
    defaultVisible: true,
    filterType: 'text',
    getValue: (c) => c.industry || null,
  },
  {
    key: 'domain',
    label: t('companies.columns.domain'),
    defaultVisible: true,
    filterType: 'text',
    getValue: (c) => c.domain || null,
  },
  {
    key: 'phone',
    label: t('companies.columns.phone'),
    defaultVisible: true,
    filterType: 'text',
    getValue: (c) => c.phone || null,
    render: (c) => c.phone ? (
      <span className="flex items-center gap-1 text-sm text-muted-foreground">
        <Phone className="h-3 w-3" /> {c.phone}
      </span>
    ) : <span className="text-muted-foreground text-sm">—</span>,
  },
  {
    key: 'email',
    label: t('companies.columns.email'),
    defaultVisible: true,
    filterType: 'text',
    getValue: (c) => c.email || null,
    render: (c) => c.email ? (
      <a
        href={`mailto:${c.email}`}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        <Mail className="h-3 w-3" /> {c.email}
      </a>
    ) : <span className="text-muted-foreground text-sm">—</span>,
  },
  {
    key: 'city',
    label: t('companies.columns.city'),
    defaultVisible: true,
    filterType: 'text',
    getValue: (c) => c.city || null,
  },
  {
    key: 'province',
    label: t('companies.columns.province'),
    defaultVisible: false,
    filterType: 'text',
    getValue: (c) => c.province || null,
  },
  {
    key: 'employeeCount',
    label: t('companies.columns.employees'),
    defaultVisible: false,
    sortKey: 'employeeCount',
    filterType: 'number-range',
    getValue: (c) => c.employee_count ?? null,
    align: 'right',
  },
  {
    key: 'annualRevenue',
    label: t('companies.columns.annualRevenue'),
    defaultVisible: false,
    sortKey: 'annualRevenue',
    filterType: 'number-range',
    getValue: (c) => c.annual_revenue ?? null,
    align: 'right',
  },
  {
    key: 'linkedinUrl',
    label: t('companies.columns.linkedin'),
    defaultVisible: false,
    filterType: 'text',
    getValue: (c) => c.linkedin_url || null,
    render: (c) => c.linkedin_url ? (
      <a
        href={c.linkedin_url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-muted-foreground hover:text-foreground truncate max-w-[150px] block"
        onClick={(e) => e.stopPropagation()}
      >
        {c.linkedin_url.replace(/^https?:\/\/(www\.)?linkedin\.com\//, '')}
      </a>
    ) : <span className="text-muted-foreground text-sm">—</span>,
  },
  {
    key: 'actions',
    label: '',
    defaultVisible: true,
    noFilter: true,
    getValue: () => '',
    className: 'w-[50px]',
  },
  ];
}

// =============================================================================
// Persistence Helpers
// =============================================================================

const FILTER_STORAGE_KEY = 'atek-crm-companies-column-filters';
const VISIBILITY_STORAGE_KEY = 'atek-crm-companies-column-visibility-v2';

// Static key list for persistence (labels not needed here)
const COLUMN_KEYS: { key: string; defaultVisible: boolean }[] = [
  { key: 'name', defaultVisible: true },
  { key: 'contactCount', defaultVisible: true },
  { key: 'type', defaultVisible: true },
  { key: 'status', defaultVisible: true },
  { key: 'industry', defaultVisible: true },
  { key: 'domain', defaultVisible: true },
  { key: 'phone', defaultVisible: true },
  { key: 'email', defaultVisible: true },
  { key: 'city', defaultVisible: true },
  { key: 'province', defaultVisible: false },
  { key: 'employeeCount', defaultVisible: false },
  { key: 'annualRevenue', defaultVisible: false },
  { key: 'linkedinUrl', defaultVisible: false },
  { key: 'actions', defaultVisible: true },
];

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMN_KEYS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

function loadColumnFilters(): Record<string, string> {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return {};
}

function saveColumnFilters(filters: Record<string, string>) {
  localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
}

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey: string | undefined;
  sortDirection: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// CrmCompaniesPage
// =============================================================================

export function CrmCompaniesPage() {
  const { t } = useTranslation('crm');
  const navigate = useNavigate();
  const COLUMNS = useMemo(() => buildColumns(t), [t]);
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<string | undefined>('name');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; label: string } | null>(null);

  // Column filters (persisted)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadColumnFilters);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadColumnVisibility);

  // Persist filters
  useEffect(() => { saveColumnFilters(columnFilters); }, [columnFilters]);
  useEffect(() => { saveColumnVisibility(columnVisibility); }, [columnVisibility]);

  // ---------------------------------------------------------------------------
  // Mode B: Server promotion
  // ---------------------------------------------------------------------------

  // Extract text filter values for server search (skip selects that start with "[")
  const columnSearchText = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([key, value]) => {
        if (!value) return false;
        if (key === 'type' || key === 'status') return false; // handled as dedicated params
        if (value.startsWith('[')) return false;
        // Skip number-range filter values (contain ">>")
        if (value.includes('>>')) return false;
        return true;
      })
      .map(([, value]) => value)
      .join(' ');
  }, [columnFilters]);

  const debouncedColumnSearch = useDebounce(columnSearchText, 300);

  // Extract type/status from select filters
  const serverType = useMemo((): CompanyType | undefined => {
    try {
      const raw = columnFilters.type;
      if (!raw) return undefined;
      const selected = JSON.parse(raw) as string[];
      return selected.length === 1 ? (selected[0] as CompanyType) : undefined;
    } catch { return undefined; }
  }, [columnFilters.type]);

  const serverStatus = useMemo((): CompanyStatus | undefined => {
    try {
      const raw = columnFilters.status;
      if (!raw) return undefined;
      const selected = JSON.parse(raw) as string[];
      return selected.length === 1 ? (selected[0] as CompanyStatus) : undefined;
    } catch { return undefined; }
  }, [columnFilters.status]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [debouncedColumnSearch, serverType, serverStatus]);

  const filters: CompanyFilters = {
    search: debouncedColumnSearch || undefined,
    type: serverType,
    status: serverStatus,
  };

  // ---------------------------------------------------------------------------
  // Queries & Mutations
  // ---------------------------------------------------------------------------
  const { data, isLoading, isError, refetch } = useCompanies(filters, page, sortKey, sortDirection);
  const createMutation = useCreateCompany();
  const updateMutation = useUpdateCompany();
  const deleteMutation = useDeleteCompany();

  const rawItems = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageSize = 25;
  const totalPages = Math.ceil(total / pageSize) || 1;

  // ---------------------------------------------------------------------------
  // Client-side filtering (number-range + multi-select with >1 value + text precision)
  // ---------------------------------------------------------------------------
  const filteredItems = useMemo(() => {
    let result = rawItems;

    // Multi-select type filter with >1 value (server only supports single)
    try {
      const raw = columnFilters.type;
      if (raw) {
        const selected = JSON.parse(raw) as string[];
        if (selected.length > 1) {
          result = result.filter((c) => selected.includes(c.type));
        }
      }
    } catch { /* ignore */ }

    // Multi-select status filter with >1 value
    try {
      const raw = columnFilters.status;
      if (raw) {
        const selected = JSON.parse(raw) as string[];
        if (selected.length > 1) {
          result = result.filter((c) => selected.includes(c.status));
        }
      }
    } catch { /* ignore */ }

    // Number-range: employeeCount
    const empRange = columnFilters.employeeCount;
    if (empRange) {
      const [minStr, maxStr] = empRange.split('>>');
      const min = minStr ? Number(minStr) : undefined;
      const max = maxStr ? Number(maxStr) : undefined;
      if (min !== undefined && !isNaN(min)) {
        result = result.filter((c) => (c.employee_count ?? 0) >= min);
      }
      if (max !== undefined && !isNaN(max)) {
        result = result.filter((c) => (c.employee_count ?? 0) <= max);
      }
    }

    // Number-range: annualRevenue
    const revRange = columnFilters.annualRevenue;
    if (revRange) {
      const [minStr, maxStr] = revRange.split('>>');
      const min = minStr ? Number(minStr) : undefined;
      const max = maxStr ? Number(maxStr) : undefined;
      if (min !== undefined && !isNaN(min)) {
        result = result.filter((c) => (c.annual_revenue ?? 0) >= min);
      }
      if (max !== undefined && !isNaN(max)) {
        result = result.filter((c) => (c.annual_revenue ?? 0) <= max);
      }
    }

    return result;
  }, [rawItems, columnFilters]);

  const items = filteredItems;

  // ---------------------------------------------------------------------------
  // Column visibility
  // ---------------------------------------------------------------------------
  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;

  const visibleColumns = useMemo(
    () => COLUMNS.filter((col) => isVisible(col.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnVisibility],
  );

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setColumnFilters({});
    setPage(0);
  };

  const hasActiveFilters = Object.values(columnFilters).some(Boolean);

  const handleCreate = useCallback(() => {
    setEditingCompany(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((company: Company) => {
    setEditingCompany(company);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(async (input: CreateCompanyInput) => {
    if (editingCompany) {
      await updateMutation.mutateAsync({ id: editingCompany.id, ...input });
    } else {
      await createMutation.mutateAsync(input);
    }
    setShowModal(false);
    setEditingCompany(null);
  }, [editingCompany, createMutation, updateMutation]);

  const handleDelete = useCallback((id: string, label: string) => {
    setPendingDelete({ id, label });
  }, []);

  const confirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, { onSettled: () => setPendingDelete(null) });
  }, [deleteMutation, pendingDelete]);

  // ---------------------------------------------------------------------------
  // Export handlers
  // ---------------------------------------------------------------------------
  const exportColumns = useMemo(
    () => visibleColumns.filter((c) => !c.noFilter),
    [visibleColumns],
  );

  const handleCopy = () => {
    copyToClipboard(items, exportColumns);
  };

  const handleCSVPage = () => {
    exportToCSV(items, exportColumns, 'crm_companies');
  };

  const handleCSVAll = async () => {
    try {
      const allData = await getCompanies(filters, 0, 10000, sortKey, sortDirection);
      exportToCSV(allData.items, exportColumns, 'crm_companies_all');
    } catch {
      toast.error(t('companies.failedToExport'));
    }
  };

  // ---------------------------------------------------------------------------
  // Filter rendering
  // ---------------------------------------------------------------------------
  const renderColumnFilter = (col: ColumnDef<Company>) => {
    if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

    // Multi-select filter
    if (col.filterType === 'select' && col.filterOptions) {
      const selected: string[] = (() => {
        try { return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : []; }
        catch { return []; }
      })();

      const toggle = (opt: string) => {
        const next = selected.includes(opt)
          ? selected.filter((o) => o !== opt)
          : [...selected, opt];
        setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
      };

      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
                {selected.length ? `${selected.length} selected` : '\u2014'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              {col.filterOptions.map((opt) => (
                <DropdownMenuCheckboxItem
                  key={opt.value}
                  checked={selected.includes(opt.value)}
                  onCheckedChange={() => toggle(opt.value)}
                >
                  {opt.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableHead>
      );
    }

    // Number-range filter
    if (col.filterType === 'number-range') {
      const raw = columnFilters[col.key] || '';
      const [minVal = '', maxVal = ''] = raw.split('>>');
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <div className="flex flex-col gap-0.5">
            <Input
              type="number"
              className="h-6 text-xs px-1"
              placeholder={t('common.min')}
              value={minVal}
              onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${maxVal}`)}
            />
            <Input
              type="number"
              className="h-6 text-xs px-1"
              placeholder={t('common.max')}
              value={maxVal}
              onChange={(e) => setColumnFilter(col.key, `${minVal}>>${e.target.value}`)}
            />
          </div>
        </TableHead>
      );
    }

    // Text filter (default)
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <Input
          className="h-7 text-xs"
          value={columnFilters[col.key] || ''}
          onChange={(e) => setColumnFilter(col.key, e.target.value)}
          placeholder="..."
        />
      </TableHead>
    );
  };

  // ---------------------------------------------------------------------------
  // Cell rendering
  // ---------------------------------------------------------------------------
  const renderCell = (col: ColumnDef<Company>, company: Company) => {
    if (col.key === 'actions') {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleEdit(company)}>
              <Pencil className="h-4 w-4 mr-2" /> {t('common.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => handleDelete(company.id, company.name)}
            >
              <Trash2 className="h-4 w-4 mr-2" /> {t('common.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    if (col.render) return col.render(company);

    const raw = col.getValue(company);
    return <span className="text-sm text-muted-foreground">{String(raw ?? '') || '\u2014'}</span>;
  };

  // ---------------------------------------------------------------------------
  // Showing text
  // ---------------------------------------------------------------------------
  const showingText = items.length < rawItems.length
    ? t('common.showingFiltered', { filtered: items.length, page: rawItems.length, total })
    : t('common.showing', { shown: Math.min(rawItems.length, total), total });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          <h1 className="text-xl font-semibold">{t('companies.pageTitle')}</h1>
          {!isLoading && (
            <Badge variant="secondary" className="ml-2">{total}</Badge>
          )}
        </div>
        <Button onClick={handleCreate} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          {t('companies.addCompany')}
        </Button>
      </div>

      <Tabs defaultValue="list" className="flex flex-col gap-4">
        <TabsList className="self-start">
          <TabsTrigger value="list">{t('companies.listTab')}</TabsTrigger>
          <TabsTrigger value="documentation">{t('companies.documentationTab')}</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="flex flex-col gap-4 mt-0">
      {/* Table Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('companies.allCompanies')}</CardTitle>
              <CardDescription>{showingText}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                  <FilterX className="h-4 w-4 mr-1" />
                  {t('common.clearFilters')}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-1" />
                {t('common.copy')}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-1" />
                    CSV
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleCSVPage}>{t('common.exportPage')}</DropdownMenuItem>
                  <DropdownMenuItem onClick={handleCSVAll}>{t('common.exportAll')}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Settings2 className="h-4 w-4 mr-1" />
                    {t('common.columns')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{t('common.toggleColumns')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {COLUMNS.filter((c) => !c.noFilter).map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={isVisible(col.key)}
                      onCheckedChange={() => toggleColumn(col.key)}
                    >
                      {col.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                {/* ROW 1: Filter inputs */}
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  {visibleColumns.map((col) => renderColumnFilter(col))}
                </TableRow>

                {/* ROW 2: Column labels + sort controls */}
                <TableRow>
                  {visibleColumns.map((col) => (
                    <TableHead
                      key={col.key}
                      className={cn(
                        col.className,
                        col.align === 'right' && 'text-right',
                      )}
                    >
                      {col.sortKey ? (
                        <SortableHeader
                          label={col.label}
                          sortKey={col.sortKey}
                          currentSortKey={sortKey}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                      ) : (
                        col.label
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody>
                {/* Loading state */}
                {isLoading && Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {visibleColumns.map((col) => (
                      <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}

                {/* Error state */}
                {!isLoading && isError && (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} className="py-8 px-4">
                      <Alert variant="destructive" className="max-w-md mx-auto">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>{t('companies.failedToLoad')}</AlertTitle>
                        <AlertDescription className="mt-1 flex items-center gap-3">
                          <span>{t('common.somethingWentWrong')}</span>
                          <Button variant="outline" size="sm" onClick={() => refetch()}>
                            <RefreshCw className="h-3.5 w-3.5 mr-1" />
                            {t('common.retry')}
                          </Button>
                        </AlertDescription>
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}

                {/* Empty state */}
                {!isLoading && !isError && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} className="text-center py-12 text-muted-foreground">
                      <Building2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      <p>{hasActiveFilters ? t('companies.noCompaniesFiltered') : t('companies.noCompanies')}</p>
                      {hasActiveFilters && (
                        <Button variant="outline" size="sm" className="mt-3" onClick={handleClearFilters}>
                          <FilterX className="h-4 w-4 mr-1" />
                          {t('common.clearFilters')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )}

                {/* Data rows */}
                {!isLoading && !isError && items.map((company) => (
                  <TableRow
                    key={company.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/crm/companies/${company.id}`)}
                  >
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col.key}
                        className={cn(
                          'text-sm',
                          col.align === 'right' && 'text-right tabular-nums',
                          col.className,
                        )}
                      >
                        {renderCell(col, company)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t px-4 py-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {t('common.page', { current: page + 1, total: totalPages })}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  {t('common.previous')}
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="documentation" className="mt-0">
          <FieldDocsTable fields={COMPANY_FIELDS} />
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('companies.deleteTitle', { name: pendingDelete?.label })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('companies.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? t('common.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit Modal */}
      <CompanyModal
        open={showModal}
        onOpenChange={(v) => { setShowModal(v); if (!v) setEditingCompany(null); }}
        company={editingCompany}
        onSave={handleSave}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}

// =============================================================================
// Company Modal (Create + Edit)
// =============================================================================

function CompanyModal({
  open,
  onOpenChange,
  company,
  onSave,
  isSaving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Company | null;
  onSave: (input: CreateCompanyInput) => Promise<void>;
  isSaving: boolean;
}) {
  const { t } = useTranslation('crm');
  const [name, setName] = useState('');
  const [type, setType] = useState<CompanyType>('prospect');
  const [status, setStatus] = useState<CompanyStatus>('active');
  const [industry, setIndustry] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [domain, setDomain] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [notes, setNotes] = useState('');

  // Populate form when editing
  useEffect(() => {
    if (open && company) {
      setName(company.name);
      setType(company.type);
      setStatus(company.status);
      setIndustry(company.industry || '');
      setWebsite(company.website || '');
      setPhone(company.phone || '');
      setEmail(company.email || '');
      setCity(company.city || '');
      setProvince(company.province || '');
      setDomain(company.domain || '');
      setLinkedinUrl(company.linkedin_url || '');
      setNotes(company.notes || '');
    } else if (open && !company) {
      setName(''); setType('prospect'); setStatus('active'); setIndustry('');
      setWebsite(''); setPhone(''); setEmail(''); setCity(''); setProvince('');
      setDomain(''); setLinkedinUrl(''); setNotes('');
    }
  }, [open, company]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({
      name,
      type,
      status,
      industry: industry || undefined,
      website: website || undefined,
      phone: phone || undefined,
      email: email || undefined,
      city: city || undefined,
      province: province || undefined,
      domain: domain || undefined,
      linkedinUrl: linkedinUrl || undefined,
      notes: notes || undefined,
    });
  };

  const isEdit = !!company;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('companies.modal.titleEdit') : t('companies.modal.titleCreate')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>{t('companies.modal.name')}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div>
              <Label>{t('companies.modal.type')}</Label>
              <Select value={type} onValueChange={(v) => setType(v as CompanyType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prospect">{t('companies.type.prospect')}</SelectItem>
                  <SelectItem value="customer">{t('companies.type.customer')}</SelectItem>
                  <SelectItem value="supplier">{t('companies.type.supplier')}</SelectItem>
                  <SelectItem value="partner">{t('companies.type.partner')}</SelectItem>
                  <SelectItem value="competitor">{t('companies.type.competitor')}</SelectItem>
                  <SelectItem value="irrelevant">{t('companies.type.irrelevant')}</SelectItem>
                  <SelectItem value="other">{t('companies.type.other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('companies.modal.status')}</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as CompanyStatus)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t('companies.status.active')}</SelectItem>
                  <SelectItem value="inactive">{t('companies.status.inactive')}</SelectItem>
                  <SelectItem value="archived">{t('companies.status.archived')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('companies.modal.industry')}</Label>
              <Input value={industry} onChange={(e) => setIndustry(e.target.value)} />
            </div>
            <div>
              <Label>{t('companies.modal.domain')}</Label>
              <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder={t('companies.modal.domainPlaceholder')} />
            </div>
            <div>
              <Label>{t('companies.modal.website')}</Label>
              <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder={t('companies.modal.websitePlaceholder')} />
            </div>
            <div>
              <Label>{t('companies.modal.linkedin')}</Label>
              <Input value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} placeholder={t('companies.modal.linkedinPlaceholder')} />
            </div>
            <div>
              <Label>{t('companies.modal.phone')}</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div>
              <Label>{t('companies.modal.email')}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label>{t('companies.modal.city')}</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
            <div>
              <Label>{t('companies.modal.province')}</Label>
              <Input value={province} onChange={(e) => setProvince(e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label>{t('companies.modal.notes')}</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={!name.trim() || isSaving}>
              {isSaving ? t('common.saving') : isEdit ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
